import React, { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { CSpinner } from '@coreui/react'
import { getCurrentUser } from './store/authSlice'
import './App.css'

// Layouts
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/pages/Login'))
const ForgotPassword = React.lazy(() => import('./views/pages/ForgotPassword'))
const ResetPassword = React.lazy(() => import('./views/pages/ResetPassword'))
const Page404 = React.lazy(() => import('./views/pages/Page404'))

const loading = (
  <div className="pt-3 text-center">
    <CSpinner color="primary" variant="grow" />
  </div>
)

// Protected Route Component
const ProtectedRoute = ({ children, isInitializing }) => {
  const { isAuthenticated, token } = useSelector((state) => state.auth)
  
  // Show loading while initializing
  if (isInitializing) {
    return loading
  }
  
  // Check if authenticated OR if token exists in localStorage
  const hasToken = token || localStorage.getItem('token')
  
  if (!isAuthenticated && !hasToken) {
    return <Navigate to="/login" replace />
  }
  
  return children
}

const App = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [isInitializing, setIsInitializing] = React.useState(true)
  const { isAuthenticated, user } = useSelector((state) => state.auth)

  useEffect(() => {
    // Initialize auth state from localStorage if not already authenticated
    const storedToken = localStorage.getItem('token')
    
    if (storedToken && !isAuthenticated) {
      // Verify token with backend
      dispatch(getCurrentUser())
        .then((action) => {
          setIsInitializing(false)
          
          // Check if user is account-admin and should be redirected
          const userData = action.payload?.data || action.payload?.user || action.payload
          if (userData?.roles?.some((role) => role.name === 'account-admin')) {
            // Auto-navigate account-admin to their account settings on first load
            navigate('/account-settings', { replace: true })
          }
        })
        .catch(() => {
          // Token is invalid, clear it
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          setIsInitializing(false)
        })
    } else {
      setIsInitializing(false)
    }
  }, [dispatch, isAuthenticated, navigate])

  // Also check after user state changes (e.g., after login)
  useEffect(() => {
    if (isAuthenticated && user && !isInitializing) {
      // Check if user is account-admin and redirect them away from super-admin-only paths
      if (user?.roles?.some((role) => role.name === 'account-admin')) {
        const accountAdminPaths = ['/dashboard', '/account-settings', '/sites', '/meters', '/zones', '/analytics']
        const isOnValidPath =
          accountAdminPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + '/')) ||
          location.pathname.startsWith('/accounts/')
        if (!isOnValidPath) {
          navigate('/account-settings', { replace: true })
        }
      }
    }
  }, [isAuthenticated, user, isInitializing, navigate, location.pathname])

  return (
    <Suspense fallback={loading}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/404" element={<Page404 />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute isInitializing={isInitializing}>
              <DefaultLayout />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  )
}

export default App
